/**
 * The size row at the top of a desktop widget's ⋯ (plan 12 §8, mockup
 * 2026-10-26 4b): the sizes its type takes of Small · Medium · Large, each
 * with a small outline of its shape, the current one highlighted. Picking
 * one closes the menu.
 */
import type { WidgetSize } from "@/components/shapes/shared/widget-face";
import { useContextMenuClose } from "@/components/ui/context-menu";
import { menuItemVariants } from "@/components/ui/dropdown-menu";
import { useT } from "@/i18n";
import { wallMessages } from "@/i18n/messages/wall";
import { cn } from "@/lib/cn";

const SIZES: ReadonlyArray<{
  size: WidgetSize;
  label: "widgetSmall" | "widgetMedium" | "widgetLarge";
  /** The outline's proportions: the size's own shape. */
  outline: string;
}> = [
  { size: "small", label: "widgetSmall", outline: "h-2.5 w-2.5" },
  { size: "medium", label: "widgetMedium", outline: "h-2.5 w-5" },
  { size: "large", label: "widgetLarge", outline: "h-5 w-5" },
];

interface WallCardMenuSizesProps {
  current: WidgetSize | null;
  /** The sizes its type takes (`widgetSizesToPick`). */
  sizes: readonly WidgetSize[];
  onPick: (size: WidgetSize) => void;
}

export function WallCardMenuSizes({
  current,
  sizes,
  onPick,
}: WallCardMenuSizesProps) {
  const t = useT(wallMessages);
  const close = useContextMenuClose();
  return (
    <div
      role="group"
      aria-label={t("widgetSize")}
      className="flex gap-appkit-2xs pb-appkit-2xs"
      data-testid="wall-card-menu-size"
    >
      {SIZES.filter(({ size }) => sizes.includes(size)).map(
        ({ size, label, outline }) => {
          const on = size === current;
          return (
            <button
              key={size}
              type="button"
              role="menuitem"
              aria-current={on ? "true" : undefined}
              className={cn(
                menuItemVariants(),
                "flex-1 flex-col justify-center gap-appkit-2xs rounded-[12px] px-0 py-appkit-xs text-appkit-mini text-primary",
                "hover:bg-[var(--color-interactive-hover)] focus:outline-none focus-visible:bg-[var(--color-interactive-hover)]",
                on && "bg-[var(--color-interactive-hover)] font-semibold",
              )}
              onClick={() => {
                close();
                if (!on) onPick(size);
              }}
              data-testid={`wall-card-menu-size-${size}`}
            >
              <span className="flex h-5 items-center" aria-hidden="true">
                <span
                  className={cn(
                    "block rounded-[3px] border-[1.5px] border-current",
                    outline,
                  )}
                />
              </span>
              {t(label)}
            </button>
          );
        },
      )}
    </div>
  );
}
